import { useTranslation } from "react-i18next";
import {
  AdjustmentsHorizontalIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

import Popover, { MenuItem } from "./Popover";
import { formatWindow } from "./reportFormat";
import { labelFor } from "../../services/enumLabels";

/** Report-specific filters → where their choices come from in filter-options. */
const EXTRA_FILTERS = {
  method: { options: (o) => (o.payment_methods ?? []).map((id) => ({ id })), kind: "payment_method" },
  source: { options: (o) => (o.booking_sources ?? []).map((id) => ({ id })), kind: "booking_source" },
  staff: { options: (o) => o.staff ?? [] },
  // Spec 03: the server sends these as `{id, name}` in the reader's language.
  service_group: { options: (o) => o.service_groups ?? [] },
  status: { options: (o) => o.appointment_statuses ?? [] },
  product_category: { options: (o) => o.product_categories ?? [] },
  brand: { options: (o) => o.brands ?? [] },
  payment_status: { options: (o) => o.payment_statuses ?? [] },
  settlement_status: { options: (o) => o.settlement_statuses ?? [] },
  reason: { options: (o) => o.refund_reasons ?? [] },
  actor: { options: (o) => o.refund_actors ?? [] },
  aging: { options: (o) => o.aging_buckets ?? [] },
  // Spec 04.
  service: { options: (o) => o.services ?? [] },
  outcome: { options: (o) => o.outcomes ?? [] },
  cancelled_by: { options: (o) => o.cancel_actors ?? [] },
  exception: { options: (o) => o.exception_types ?? [] },
  // Spec 05.
  account: { options: (o) => o.account_types ?? [] },
  value_band: { options: (o) => o.value_bands ?? [] },
  consent: { options: (o) => o.consent_states ?? [] },
  frequency: { options: (o) => o.frequency_bands ?? [] },
  // Spec 06.
  stock_state: { options: (o) => o.stock_states ?? [] },
  movement_type: { options: (o) => o.movement_types ?? [] },
  rating: { options: (o) => (o.ratings ?? []).map((r) => ({ ...r, id: String(r.id) })) },
  has_photo: { options: (o) => o.photo_options ?? [] },
};

/** Every report-specific list filter the bar knows — the page's URL keys. */
export const EXTRA_FILTER_KEYS = Object.keys(EXTRA_FILTERS);

/**
 * The report card's filter bar (spec 02 §4).
 *
 * Edits a *draft*: nothing is fetched until Apply, which writes the draft to
 * the URL. The pickers only offer what filter-options returned — the reader's
 * own branches and currencies — and the server re-checks all of it anyway.
 */
export default function ReportFilterBar({
  draft,
  setDraft,
  options,
  report,
  meta,
  dirty,
  onApply,
  pinnedToday,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const periods = options?.periods ?? [];
  const compareModes = options?.compare_modes ?? [];
  const branches = options?.branches ?? [];
  const currencies = options?.currencies ?? [];

  /* ---------------------------------------------------------------- date */

  const custom = !draft.period;
  const appliedWindow = meta?.window;
  const dateLabel = pinnedToday
    ? t("reports.periods.today")
    : !custom && meta?.period === draft.period && appliedWindow
      ? formatWindow(appliedWindow, locale)
      : custom && draft.from && draft.to
        ? formatWindow({ from: draft.from, to: draft.to }, locale)
        : t(`reports.periods.${draft.period || "custom"}`);

  /* -------------------------------------------------------------- branch */

  const branchLabel =
    draft.branches.length === 0
      ? t("reports.filters.allBranches")
      : draft.branches.length === 1
        ? (branches.find((b) => b.id === draft.branches[0])?.name ??
          t("reports.shell.branchesCount", { count: 1 }))
        : t("reports.shell.branchesCount", { count: draft.branches.length });

  const toggleBranch = (id) =>
    update({
      branches: draft.branches.includes(id)
        ? draft.branches.filter((b) => b !== id)
        : [...draft.branches, id],
    });

  /* ------------------------------------------------------------ currency */

  const currencyValue = draft.currency || meta?.currency || options?.default_currency || "";
  const currencyLabel =
    draft.currency === "all"
      ? t("reports.shell.allCurrencies")
      : t("reports.shell.currencyValue", { currency: currencyValue || "—" });

  /* --------------------------------------------------------------- extra */

  const extras = (report?.filters ?? []).filter((name) => EXTRA_FILTERS[name]);
  const extraCount =
    extras.reduce((n, name) => n + ((draft[name] ?? []).length > 0 ? 1 : 0), 0) +
    (draft.refund_view === "refund_date" ? 1 : 0) +
    (draft.date_basis ? 1 : 0) +
    (draft.inactive_days ? 1 : 0);
  // Spec 04: appointment date basis, default first (appointment date).
  const dateBases = report?.date_bases ?? [];
  // Spec 05 (R23): one number of days, presets + custom; default 60.
  const inactive = (report?.filters ?? []).includes("inactive_days");
  const inactivePresets = options?.inactive_days ?? [30, 60, 90];
  const hasExtras = extras.length > 0 || report?.cash_view || dateBases.length > 1 || inactive;

  const toggleExtra = (name, id) => {
    const list = draft[name] ?? [];
    update({ [name]: list.includes(id) ? list.filter((v) => v !== id) : [...list, id] });
  };

  const extraOptionLabel = (name, option) =>
    option.name ?? labelFor(EXTRA_FILTERS[name].kind ?? name, option.id);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Date range with presets */}
      <Popover
        icon={CalendarDaysIcon}
        label={dateLabel}
        disabled={pinnedToday}
        buttonClassName="min-w-[12rem]"
        panelClassName="w-72"
      >
        {(close) => (
          <div>
            {periods.map((period) => (
              <MenuItem
                key={period}
                selected={draft.period === period}
                onClick={() => {
                  update({ period, from: "", to: "" });
                  close();
                }}
              >
                {t(`reports.periods.${period}`)}
              </MenuItem>
            ))}
            <MenuItem selected={custom} onClick={() => update({ period: "" })}>
              {t("reports.periods.custom")}
            </MenuItem>

            {custom && (
              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-line px-1 pt-2">
                <label className="text-caption text-ink-2">
                  {t("reports.filters.from")}
                  <input
                    type="date"
                    className="field-input mt-1"
                    value={draft.from}
                    max={draft.to || undefined}
                    onChange={(e) => update({ from: e.target.value })}
                  />
                </label>
                <label className="text-caption text-ink-2">
                  {t("reports.filters.to")}
                  <input
                    type="date"
                    className="field-input mt-1"
                    value={draft.to}
                    min={draft.from || undefined}
                    onChange={(e) => update({ to: e.target.value })}
                  />
                </label>
              </div>
            )}
          </div>
        )}
      </Popover>

      {/* Branches (authorized only — filter-options is server-scoped) */}
      {branches.length > 1 && (
        <Popover icon={BuildingStorefrontIcon} label={branchLabel} panelClassName="w-72">
          <div className="max-h-72 overflow-y-auto">
            <MenuItem selected={draft.branches.length === 0} onClick={() => update({ branches: [] })}>
              {t("reports.filters.allBranches")}
            </MenuItem>
            {branches.map((branch) => (
              <label
                key={branch.id}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-dense hover:bg-raised"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--pl-accent)]"
                  checked={draft.branches.includes(branch.id)}
                  onChange={() => toggleBranch(branch.id)}
                />
                <span className="flex-1 truncate">{branch.name}</span>
                {branch.currency && <span className="text-caption text-ink-2">{branch.currency}</span>}
              </label>
            ))}
          </div>
        </Popover>
      )}

      {/* Currency — money reports only */}
      {report?.finance && currencies.length > 0 && (
        <Popover icon={CurrencyDollarIcon} label={currencyLabel} panelClassName="w-56">
          {(close) => (
            <div>
              {currencies.map((code) => (
                <MenuItem
                  key={code}
                  selected={draft.currency !== "all" && currencyValue === code}
                  onClick={() => {
                    update({ currency: code === options?.default_currency ? "" : code });
                    close();
                  }}
                >
                  <span dir="ltr">{code}</span>
                </MenuItem>
              ))}
              {currencies.length > 1 && (
                <MenuItem
                  selected={draft.currency === "all"}
                  onClick={() => {
                    update({ currency: "all" });
                    close();
                  }}
                >
                  {t("reports.shell.allCurrencies")}
                </MenuItem>
              )}
            </div>
          )}
        </Popover>
      )}

      {/* Comparison */}
      <Popover
        icon={ChartBarIcon}
        label={t("reports.shell.compareValue", { mode: t(`reports.compare.${draft.compare}`) })}
        panelClassName="w-64"
      >
        {(close) => (
          <div>
            {compareModes.map((mode) => (
              <MenuItem
                key={mode}
                selected={draft.compare === mode}
                onClick={() => {
                  update({ compare: mode });
                  close();
                }}
              >
                {t(`reports.compare.${mode}`)}
              </MenuItem>
            ))}
          </div>
        )}
      </Popover>

      {/* Report-specific filters */}
      {hasExtras && (
        <Popover
          icon={AdjustmentsHorizontalIcon}
          label={
            extraCount > 0
              ? t("reports.shell.moreFiltersCount", { count: extraCount })
              : t("reports.shell.moreFilters")
          }
          panelClassName="w-80"
        >
          <div className="max-h-96 space-y-3 overflow-y-auto p-1">
            {inactive && (
              <fieldset>
                <legend className="mb-1 text-caption font-semibold text-ink-2">
                  {t("reports.shell.inactiveDays")}
                </legend>
                {inactivePresets.map((days) => (
                  <label key={days} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-dense">
                    <input
                      type="radio"
                      name="inactive_days"
                      className="h-4 w-4 accent-[var(--pl-accent)]"
                      checked={Number(draft.inactive_days || 60) === days}
                      onChange={() => update({ inactive_days: days === 60 ? "" : String(days) })}
                    />
                    {t("reports.shell.moreThanDays", { count: days })}
                  </label>
                ))}
                <label className="flex items-center gap-2 px-2 py-1.5 text-dense">
                  {t("reports.shell.customDays")}
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    className="field-input w-24"
                    value={
                      inactivePresets.includes(Number(draft.inactive_days || 60)) ? "" : draft.inactive_days
                    }
                    onChange={(e) => update({ inactive_days: e.target.value.replace(/\D/g, "") })}
                  />
                </label>
              </fieldset>
            )}

            {dateBases.length > 1 && (
              <fieldset>
                <legend className="mb-1 text-caption font-semibold text-ink-2">
                  {t("reports.shell.dateBasis")}
                </legend>
                {dateBases.map((basis, index) => (
                  <label
                    key={basis}
                    className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-dense"
                  >
                    <input
                      type="radio"
                      name="date_basis"
                      className="h-4 w-4 accent-[var(--pl-accent)]"
                      checked={(draft.date_basis || dateBases[0]) === basis}
                      onChange={() => update({ date_basis: index === 0 ? "" : basis })}
                    />
                    {t(`reports.shell.dateBasisChoices.${basis}`, {
                      defaultValue: labelFor("date_basis", basis),
                    })}
                  </label>
                ))}
              </fieldset>
            )}

            {report?.cash_view && (
              <fieldset>
                <legend className="mb-1 text-caption font-semibold text-ink-2">
                  {t("reports.shell.refundView")}
                </legend>
                {["original", "refund_date"].map((mode) => (
                  <label key={mode} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-dense">
                    <input
                      type="radio"
                      name="refund_view"
                      className="h-4 w-4 accent-[var(--pl-accent)]"
                      checked={(draft.refund_view || "original") === mode}
                      onChange={() => update({ refund_view: mode === "original" ? "" : mode })}
                    />
                    {t(`reports.shell.refundViews.${mode}`)}
                  </label>
                ))}
              </fieldset>
            )}

            {extras.map((name) => {
              const choices = EXTRA_FILTERS[name].options(options ?? {});

              if (choices.length === 0) return null;

              return (
                <fieldset key={name}>
                  <legend className="mb-1 text-caption font-semibold text-ink-2">
                    {t(`reports.shell.extraFilters.${name}`)}
                  </legend>
                  {choices.map((option) => (
                    <label
                      key={option.id}
                      className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-dense"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--pl-accent)]"
                        checked={(draft[name] ?? []).includes(option.id)}
                        onChange={() => toggleExtra(name, option.id)}
                      />
                      <span className="truncate">{extraOptionLabel(name, option)}</span>
                    </label>
                  ))}
                </fieldset>
              );
            })}
          </div>
        </Popover>
      )}

      <button type="button" className="btn-primary" disabled={!dirty} onClick={onApply}>
        {t("reports.shell.apply")}
      </button>
    </div>
  );
}
